import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { uniqBy } from 'lodash';

import type {
  ArrivalTimeUpdateFilterEvent,
  DepartureTimeUpdateFilterEvent,
  CarriersFilterUpdateFilterEvent,
  FerryFilterEvents,
  FerryFilters,
} from '@apps/main/new-desktop/modules/ferryFilters';
import { FerryFilterType } from '@apps/main/new-desktop/modules/ferryFilters';
import { FerryIngredient } from '@apps/main/modules/recipe/ingredients';

import type { CarrierName, IngredientId } from '@libs/trip-planner-interfaces';
import { propertyStringComparer } from '@libs/utils';

import { useRecipeStore } from '../recipe/recipeStore';
import { useTripStore } from '../trip/tripStore';

import {
  getArrivalTimeRangeSliderFilter,
  getCarriersCheckboxFilter,
  getDefaultFilters,
  getDepartureTimeRangeSliderFilter,
  getStringRangeType,
} from './ferryFiltersStoreLogic';
import { getFilterKeyFromTagId } from './flightsFiltersStoreLogic';
import { FerryFilterName } from './ferryFilter.model';

export const useFerryFiltersStore = defineStore('FerryFiltersStore', () => {
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();

  //Helper getters
  const allowedFerryIngredients = computed<FerryIngredient[]>(() => {
    if (allowedFerryIngredientIds.value?.length) {
      return recipeStore.ferryOnlyRecipe.filter((ferryIngredient) =>
        allowedFerryIngredientIds.value?.includes(ferryIngredient.ingredientId)
      );
    }
    return recipeStore.ferryOnlyRecipe;
  });

  //Helper state

  const allowedFerryIngredientIds = ref<IngredientId[] | null>(null);

  // State

  const isPanelOpened = ref<boolean>(false);

  // Actions

  function openPanel(): void {
    isPanelOpened.value = true;
  }

  function closePanel(): void {
    isPanelOpened.value = false;
  }

  function update(action: FerryFilterEvents): void {
    switch (action.filterType) {
      case FerryFilterType.ArrivalTime: {
        onUpdateArrivalTimeFilter(action);
        break;
      }
      case FerryFilterType.DepartureTime: {
        onUpdateDepartureTimeFilter(action);
        break;
      }
      case FerryFilterType.Carriers: {
        onUpdateCarriersFilter(action);
        break;
      }
    }
  }

  function updateAll(actions: FerryFilterEvents[]): void {
    actions.forEach(update);
  }

  function resetAllFilters(): void {
    allowedFerryIngredientIds.value?.forEach((ingerdientId) => {
      const ferryIngredient = recipeStore.getFerryIngredient(ingerdientId);

      ferryIngredient.filters = FerryIngredient.defaultFilters;
    });
  }

  function setAllowedFerryIngredientIds(ingredientIds: IngredientId[]): void {
    allowedFerryIngredientIds.value = ingredientIds;
  }

  function clearAllowedFerryIngredientIds(): void {
    allowedFerryIngredientIds.value = null;
  }

  function removeFilter(tagId: string, ingredientId: IngredientId): void {
    const ferryIngredient = recipeStore.getFerryIngredient(ingredientId);
    const filterKey = getFilterKeyFromTagId(tagId);

    if (filterKey === FerryFilterName.CarrierFilters) {
      const carrierCode = tagId.split('.')[1];

      if (carrierCode) {
        // HACK: The only way I found to update carrier filters
        ferryIngredient.filters.carrierFilters = [
          ...ferryIngredient.filters.carrierFilters,
        ];

        const carrierCodeIndex =
          ferryIngredient.filters.carrierFilters.findIndex(
            (code) => code === carrierCode
          );

        ferryIngredient.filters.carrierFilters.splice(carrierCodeIndex, 1);
      }
    } else {
      ferryIngredient.filters[tagId] = FerryIngredient.defaultFilters[tagId];
    }
  }

  // Helper functions

  function onUpdateArrivalTimeFilter(
    action: ArrivalTimeUpdateFilterEvent
  ): void {
    const ingredient = recipeStore.getFerryIngredient(action.data.ingredientId);
    ingredient.filters.arrivalTimeFilters = getStringRangeType(
      action.data.range
    );
  }

  function onUpdateDepartureTimeFilter(
    action: DepartureTimeUpdateFilterEvent
  ): void {
    const ingredient = recipeStore.getFerryIngredient(action.data.ingredientId);

    ingredient.filters.departureTimeFilters = getStringRangeType(
      action.data.range
    );
  }

  function onUpdateCarriersFilter(
    action: CarriersFilterUpdateFilterEvent
  ): void {
    const selectedCarrier = action.data.selected;
    const ferryIngredient = recipeStore.getFerryIngredient(
      action.data.ingredientId
    );

    // HACK: The only way I found to update carrier filters
    ferryIngredient.filters.carrierFilters = [
      ...ferryIngredient.filters.carrierFilters,
    ];
    const carrierIndex =
      ferryIngredient.filters.carrierFilters.indexOf(selectedCarrier);
    if (carrierIndex > -1) {
      ferryIngredient.filters.carrierFilters.splice(carrierIndex, 1);
    } else {
      ferryIngredient.filters.carrierFilters.push(selectedCarrier);
    }
  }

  // Getters

  const filters = computed<FerryFilters | null>(() => {
    if (allowedFerryIngredients.value?.length === 0) {
      return null;
    }

    const newFilters: FerryFilters = getDefaultFilters();

    allowedFerryIngredients.value.forEach((ferry) => {
      newFilters.ingredientId = ferry.ingredientId; // TODO: Consider using array of ingredientIds here. That will be buggy if we want to display filters fo 2 from 3 ferries and we want only to reset the filters for two of them not only last ingredient saved
      newFilters.arrivalTime.filters.push(
        getArrivalTimeRangeSliderFilter(ferry)
      );

      newFilters.departureTime.filters.push(
        getDepartureTimeRangeSliderFilter(ferry)
      );

      const ferryCarriers = tripStore.getFerryAllCarriers(
        ferry.ingredientId
      ).allCarriers;

      const uniqueCarriers = uniqBy(ferryCarriers, 'name').sort(
        propertyStringComparer((carrier: CarrierName) => carrier.name)
      );
      newFilters.carriers.data.push({
        title: `${ferry.fromPortText} - ${ferry.toPortText}`,
        filters: getCarriersCheckboxFilter(uniqueCarriers, ferry),
        ingredientId: ferry.ingredientId,
      });
    });

    return newFilters;
  });

  return {
    // State
    isPanelOpened,

    // Actions
    closePanel,
    openPanel,
    update,
    updateAll,
    resetAllFilters,
    removeFilter,
    setAllowedFerryIngredientIds,
    clearAllowedFerryIngredientIds,

    // Getters
    filters,
  };
});
